import LegalLayout, { Section } from "./LegalLayout";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" testId="privacy-page">
      <Section title="Overview">
        <p>
          pragti (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) builds a digital twin that helps you
          stay on top of work and personal life. This policy explains what information we
          collect, how we use it, and the choices you have.
        </p>
        <p>
          Contact:{" "}
          <a href="mailto:privacy@pragti.ai" className="text-pragti-accent hover:underline">
            privacy@pragti.ai
          </a>
        </p>
      </Section>

      <Section title="Information we collect">
        <p>
          <strong className="text-pragti-text">Account information.</strong> When you sign in
          with Google, we receive your name, email address, and profile information needed to
          create and secure your account.
        </p>
        <p>
          <strong className="text-pragti-text">Google Workspace data (with your consent).</strong>{" "}
          If you connect Google apps, we access only the data required for features you turn
          on. This may include Gmail messages and metadata, Google Calendar events, Google
          Drive files you choose to sync, Google Docs/Sheets/Slides content, and Google
          Contacts. We access this data only after you authorize each connection in Google&apos;s
          consent screen.
        </p>
        <p>
          <strong className="text-pragti-text">Twin and conversation data.</strong> Content you
          provide while setting up or talking to your twin, including voice recordings,
          corrections, and preferences.
        </p>
        <p>
          <strong className="text-pragti-text">Waitlist and support.</strong> Email address and
          any information you send when joining the waitlist or contacting support.
        </p>
        <p>
          <strong className="text-pragti-text">Usage and device data.</strong> Basic logs such as
          IP address, browser type, pages visited, and error reports to keep the service
          secure and reliable.
        </p>
      </Section>

      <Section title="How we use information">
        <p>We use your information to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide, maintain, and improve the pragti digital twin</li>
          <li>Answer questions and act on your behalf using context you authorize</li>
          <li>Sync and search the Google data you connect, only for your twin</li>
          <li>Send product updates, waitlist invitations, and support replies</li>
          <li>Protect against abuse, fraud, and security incidents</li>
        </ul>
        <p>
          We do <strong className="text-pragti-text">not</strong> sell your personal information.
          We do <strong className="text-pragti-text">not</strong> use Google user data for
          advertising or to train general-purpose models unrelated to your twin.
        </p>
      </Section>

      <Section title="Google API Services User Data Policy">
        <p>
          pragti&apos;s use of information received from Google APIs adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="text-pragti-accent hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <p>Specifically:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            Google user data is used only to provide or improve user-facing features you
            request, such as answering questions about your email, calendar, or documents.
          </li>
          <li>
            We do not transfer Google user data to third parties except service providers
            that process data on our behalf under contract, or when required by law.
          </li>
          <li>
            Human access to Google user data is limited to security, support, and legal
            compliance, with appropriate controls.
          </li>
          <li>
            AI features use your data to personalize <em>your</em> twin, not to build
            unrelated products.
          </li>
        </ul>
      </Section>

      <Section title="Google scopes we may request">
        <p>When you connect Google services, we may request scopes such as:</p>
        <ul className="list-disc pl-6 space-y-2 text-sm">
          <li>Sign-in: openid, email, profile</li>
          <li>Gmail: read, compose, send, and modify messages you authorize</li>
          <li>Calendar: read and manage events you authorize</li>
          <li>Drive, Docs, Sheets, Slides: access files you authorize</li>
          <li>Contacts: read and manage contacts you authorize</li>
        </ul>
        <p>
          You can disconnect any Google app from pragti settings. Disconnecting stops new
          syncs and removes stored tokens on our side.
        </p>
      </Section>

      <Section title="Retention and deletion">
        <p>
          We keep data only as long as needed to provide the service or meet legal
          obligations. When you disconnect a Google app or delete your account, we delete
          associated OAuth tokens and begin removal of synced Google data from our systems
          within a reasonable period, subject to backup retention limits.
        </p>
        <p>
          To request deletion, email{" "}
          <a href="mailto:privacy@pragti.ai" className="text-pragti-accent hover:underline">
            privacy@pragti.ai
          </a>{" "}
          from the address tied to your account.
        </p>
      </Section>

      <Section title="Security">
        <p>
          We use encryption in transit (HTTPS/TLS), access controls, and industry-standard
          practices to protect your data. OAuth tokens are stored securely and are not
          exposed in client-side code.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          Depending on where you live, you may have rights to access, correct, delete, or
          export your personal data, and to object to certain processing. Contact us at{" "}
          <a href="mailto:privacy@pragti.ai" className="text-pragti-accent hover:underline">
            privacy@pragti.ai
          </a>
          .
        </p>
      </Section>

      <Section title="Changes">
        <p>
          We may update this policy from time to time. We will post the new version on this
          page and update the date above. Continued use of pragti after changes means you
          accept the updated policy.
        </p>
      </Section>
    </LegalLayout>
  );
}
